import { useEffect, useState } from 'react';
import type { EmailConfig, EmailDriverKind } from '@cornerstone/shared';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import { BoPage, Section, ErrorBanner, OkBanner, inputCls, btnPrimary, btnGhost, errMsg } from './ui';

// =============================================================================
// BO · 메일 발송(SMTP) 설정 (2026-09-22 대교: ".env에서 세팅해야 하는데 SMTP 설정을
// 운영사 설정에서 할 수 있으면 좋겠음")
//
// 운영사 전용. 관리자 추가·비밀번호 초기화·장애 알림 메일이 이 설정으로 나간다.
// 저장 즉시 다음 발송부터 반영(재시작 불필요). 비밀번호는 서버에 암호화 저장되며 다시
// 표시되지 않는다 — AI 모델 설정의 API 키와 같은 규칙. 저장값이 없으면 서버 .env 값을
// 그대로 보여 주고(source=env), 저장하면 .env 보다 우선한다.
// =============================================================================

const DRIVERS: ReadonlyArray<{ value: EmailDriverKind; label: string; hint: string }> = [
  { value: 'smtp', label: 'SMTP로 발송', hint: 'Gmail은 smtp.gmail.com · 587 · 앱 비밀번호(16자)를 씁니다. 계정 비밀번호가 아닙니다.' },
  { value: 'console', label: '발송 끔 (서버 로그에만 기록)', hint: '메일을 보내지 않고 서버 로그에 내용을 남깁니다. 개발·점검용.' },
];
const PORTS = [587, 465] as const;

export function EmailSettings(): JSX.Element {
  const [driver, setDriver] = useState<EmailDriverKind>('smtp');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(587);
  const [user, setUser] = useState('');
  const [from, setFrom] = useState('');
  const [password, setPassword] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [source, setSource] = useState<EmailConfig['source']>('env');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  function apply(cfg: EmailConfig): void {
    setDriver(cfg.driver); setHost(cfg.host); setPort(cfg.port); setUser(cfg.user); setFrom(cfg.from);
    setHasPassword(cfg.hasPassword); setSource(cfg.source); setUpdatedAt(cfg.updatedAt);
    setPassword('');
  }

  async function load() {
    setLoading(true);
    try { apply(await api.boGetEmailConfig()); setLoaded(true); setErr(null); }
    catch (e) { setErr(errMsg(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  async function save() {
    setSaving(true); setErr(null); setOk(null);
    try {
      const saved = await api.boSetEmailConfig({
        driver, host: host.trim(), port, user: user.trim(), from: from.trim(),
        // 빈 입력이면 비밀번호 미변경(undefined).
        ...(password.trim() ? { password: password.trim() } : {}),
      });
      apply(saved);
      setOk('저장되었습니다. 다음 발송부터 이 설정을 씁니다.');
    } catch (e) { setErr(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function reset() {
    setSaving(true); setErr(null); setOk(null);
    try { apply(await api.boResetEmailConfig()); setOk('저장값을 지웠습니다. 서버 .env 설정으로 동작합니다.'); }
    catch (e) { setErr(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function sendTest() {
    const to = testTo.trim();
    if (!to.includes('@')) { setErr('받는 사람 이메일을 입력하세요.'); return; }
    setTesting(true); setErr(null); setOk(null);
    try {
      const result = await api.boTestEmail(to);
      setOk(result.ok
        ? `${to} 로 테스트 메일을 보냈습니다 (${result.driver === 'smtp' ? 'SMTP' : '서버 로그'} · ${result.source === 'stored' ? '저장된 설정' : '.env 설정'}). 받은 편지함을 확인하세요.`
        : '발송 결과를 확인하지 못했습니다.');
    } catch (e) {
      const m = errMsg(e);
      setErr(/<!doctype|<html|cloudflare|52[0-9]\b|504|gateway time-?out/i.test(m)
        ? '서버가 제때 응답하지 않아 프록시가 오류 페이지를 돌려줬습니다. 서버에서 SMTP 서버(587/465)로 나가는 연결이 막혀 있을 가능성이 큽니다. 서버 관리자에게 아웃바운드 포트를 확인해 달라고 하세요.'
        : /45초 안에 응답하지 않았습니다|connection timeout|greeting never received|socket timeout/i.test(m)
          ? `SMTP 서버에 연결했지만 응답이 없습니다. 나가는 포트가 막혀 있거나 호스트·포트가 다릅니다. (${m.slice(0, 120)})`
          : /535|auth|invalid login|username and password/i.test(m)
        ? `인증에 실패했습니다. 계정과 비밀번호(Gmail은 앱 비밀번호)를 확인하세요. (${m.slice(0, 120)})`
        : /ENOTFOUND|ECONNREFUSED|ETIMEDOUT|getaddrinfo/i.test(m)
          ? `SMTP 서버에 연결하지 못했습니다. 호스트·포트를 확인하세요. (${m.slice(0, 120)})`
          : `발송 실패: ${m.slice(0, 200)}`);
    } finally { setTesting(false); }
  }

  const meta = DRIVERS.find((d) => d.value === driver) ?? DRIVERS[0]!;
  const smtp = driver === 'smtp';
  const needsPassword = smtp && !hasPassword && !password.trim();

  return (
    <BoPage
      title="메일 발송 설정"
      subtitle="관리자 추가·비밀번호 초기화·장애 알림 메일이 이 설정으로 나갑니다. 저장 즉시 반영(재시작 불필요)."
      actions={<button type="button" className={btnGhost} onClick={() => void load()} disabled={loading}>새로고침</button>}
    >
      <ErrorBanner message={err} />
      <OkBanner message={ok} />
      <Section title="SMTP">
        {loading && !loaded && !err ? <ContentSkeleton label="메일 설정을 불러오는 중…" variant="form" /> : (
        <div className="flex flex-col gap-4 max-w-[560px]">
          <p role="status" className="rounded border border-line bg-panel-2 p-3 text-[13px] text-text-dim">
            {source === 'stored'
              ? <>화면에서 저장한 설정을 쓰고 있습니다{updatedAt ? ` · ${new Date(updatedAt).toLocaleString('ko-KR')}` : ''}. 서버 .env 값보다 우선합니다.</>
              : <>아직 화면에서 저장한 설정이 없어 서버 .env 값을 쓰고 있습니다. 아래 값을 고쳐 저장하면 그때부터 이 설정이 우선합니다.</>}
          </p>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">발송 방식</span>
            <select className={inputCls} value={driver} onChange={(e) => setDriver(e.target.value as EmailDriverKind)}>
              {DRIVERS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <span className="text-[12px] text-text-faint">{meta.hint}</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">SMTP 호스트</span>
            <input className={inputCls} value={host} placeholder="smtp.gmail.com" disabled={!smtp} onChange={(e) => setHost(e.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">포트</span>
            <div className="flex flex-wrap items-center gap-2">
              {PORTS.map((p) => (
                <button key={p} type="button" className={btnGhost} disabled={!smtp} aria-pressed={port === p} onClick={() => setPort(p)}
                  style={port === p ? { borderColor: 'var(--color-accent)', color: 'var(--color-accent)' } : undefined}>
                  {p} {p === 587 ? '(STARTTLS)' : '(TLS)'}
                </button>
              ))}
              <input className={`${inputCls} w-[120px]`} type="number" min={1} max={65535} value={port} disabled={!smtp} aria-label="포트 직접 입력"
                onChange={(e) => { const n = Number(e.target.value); if (Number.isInteger(n) && n > 0 && n <= 65535) setPort(n); }} />
            </div>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">계정 (보내는 메일 주소)</span>
            <input className={inputCls} value={user} placeholder="h9interaction@gmail.com" disabled={!smtp} autoComplete="off" onChange={(e) => setUser(e.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">
              비밀번호 {smtp && hasPassword && <span className="text-green">· 저장됨</span>}
            </span>
            <input className={inputCls} type="password" value={password} disabled={!smtp} autoComplete="new-password"
              placeholder={hasPassword ? '변경하려면 새 값 입력 (비우면 유지)' : 'Gmail 앱 비밀번호 16자'}
              onChange={(e) => setPassword(e.target.value)} />
            {needsPassword && <span className="text-[12px] text-red">SMTP로 보내려면 비밀번호가 필요합니다.</span>}
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">보내는 사람 표시 (선택)</span>
            <input className={inputCls} value={from} placeholder="BRICK lit <h9interaction@gmail.com>" disabled={!smtp} onChange={(e) => setFrom(e.target.value)} />
            <span className="text-[12px] text-text-faint">비우면 계정 주소로 표시됩니다.</span>
          </label>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="button" className={btnPrimary} onClick={() => void save()} disabled={saving || loading || needsPassword}
              title={needsPassword ? 'SMTP 비밀번호를 입력하세요.' : undefined}>
              {saving ? '저장 중…' : '저장'}
            </button>
            <button type="button" className={btnGhost} onClick={() => void reset()} disabled={saving || loading || source !== 'stored'}
              title={source !== 'stored' ? '저장된 값이 없습니다. 지금도 .env 설정으로 동작합니다.' : undefined}>
              저장값 지우고 .env로 되돌리기
            </button>
          </div>
        </div>
        )}
      </Section>

      <Section title="테스트 메일">
        <div className="flex flex-col gap-2 max-w-[560px]">
          <p className="text-[13px] text-text-dim">지금 적용 중인 설정으로 한 통 보냅니다. 저장하지 않은 입력은 쓰이지 않으니 먼저 저장하세요.</p>
          <div className="flex flex-wrap items-center gap-2">
            <input className={`${inputCls} flex-1 min-w-[240px]`} type="email" value={testTo} placeholder="받는 사람 이메일" aria-label="테스트 메일 받는 사람"
              onChange={(e) => setTestTo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void sendTest(); }} />
            <button type="button" className={btnGhost} onClick={() => void sendTest()} disabled={testing || loading || saving}>
              {testing ? '보내는 중…' : '테스트 메일 보내기'}
            </button>
          </div>
        </div>
      </Section>
    </BoPage>
  );
}
